import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { buildCanonicalUrl } from '../../utils/seoUtils';

const ContactPage = ({ config }) => {
  const company = config?.company || {};
  const social = config?.social || {};

  const contactMethods = [
    {
      title: "Email",
      value: company.contact_email || "contact@kadellabs.com",
      icon: "📧",
      link: `mailto:${company.contact_email || "contact@kadellabs.com"}`
    }
  ];

  const socialMedia = [
    {
      name: "LinkedIn",
      icon: "💼",
      url: social.linkedin || "https://linkedin.com/company/ultralender-ai",
      description: "Connect with us professionally"
    },
    {
      name: "Twitter",
      icon: "🐦",
      url: social.twitter || "https://twitter.com/ultralender_ai",
      description: "Follow for updates and insights"
    },
    {
      name: "Telegram",
      icon: "✈️",
      url: social.telegram || "https://t.me/ultralender_ai",
      description: "Join our community"
    },
    {
      name: "WhatsApp",
      icon: "💬",
      url: social.whatsapp || "https://wa.me/15558587224",
      description: "Direct messaging support"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Contact Us"
        description="Get in touch with the KL Accelerators team. Connect via email or social channels to discuss enterprise accelerator solutions for your organization."
        canonicalUrl={buildCanonicalUrl('/contact')}
        keywords="contact KL Accelerators, enterprise support, accelerator consultation"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Ready to accelerate your development with our proven solutions? 
              Connect with our team through any of these channels.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Methods */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Our Team</h2>
            <p className="text-lg text-gray-600">Ready to accelerate your development with our proven solutions? Let's discuss your needs.</p>
          </div>

          <div className="max-w-2xl mx-auto mb-16">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                className="bg-white rounded-xl shadow-lg p-12 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center block border-2 border-transparent hover:border-primary-200"
              >
                <div className="text-6xl mb-6">{method.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">{method.title}</h3>
                <p className="text-primary-600 font-medium text-xl mb-4">{method.value}</p>
                <div className="bg-primary-50 rounded-lg p-4 text-center">
                  <p className="text-primary-700 font-medium">
                    💬 Click to send us an email directly
                  </p>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="flex items-center justify-center space-x-2">
                    <span>⚡</span>
                    <span>Quick Response</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <span>🤝</span>
                    <span>Personal Support</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <span>📋</span>
                    <span>Detailed Proposals</span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Social Media */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Follow Our Journey</h2>
            <p className="text-lg text-gray-600 mb-8">Stay connected across all platforms</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {socialMedia.map((platform, index) => (
              <a
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
              >
                <div className="text-3xl mb-3">{platform.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{platform.name}</h3>
                <p className="text-gray-600 text-sm">{platform.description}</p>
              </a>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 bg-primary-50 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-primary-900 mb-4">Ready to Get Started?</h3>
            <p className="text-primary-700 mb-6 max-w-2xl mx-auto">
              Whether you're looking to implement existing accelerators or need a custom solution, 
              our team is here to help accelerate your development and digital transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/browse"
                className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Browse Our Accelerators
              </Link>
              <Link
                to="/custom"
                className="border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-600 hover:text-white transition-colors"
              >
                Request Custom Solution
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;